import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './regex-tester.component.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import { PersistedInput } from '../../../utils/persisted-input';

const STORAGE_KEY = 'regex-tester';
type PersistedRegex = { pattern: string; testString: string; flags: Record<string, boolean> };
const isValidPersisted = (v: unknown): v is PersistedRegex => {
    if (!v || typeof v !== 'object') return false;
    const o = v as any;
    if (typeof o.pattern !== 'string' || typeof o.testString !== 'string') return false;
    if (!o.flags || typeof o.flags !== 'object') return false;
    // Reject persisted pattern that no longer parses (e.g. flags from a future build).
    try { new RegExp(o.pattern, Object.entries(o.flags).filter(([, v]) => v).map(([k]) => k).join('')); } catch { return false; }
    return true;
};

interface RegexState {
    pattern: string;
    testString: string;
    flags: { g: boolean; i: boolean; m: boolean; s: boolean; u: boolean };
    error: string;
    copiedField: string | null;
}

const PRESETS: { label: string; pattern: string; flags?: string }[] = [
    { label: 'Select a preset...', pattern: '' },
    { label: 'Email', pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}' },
    { label: 'URL', pattern: 'https?:\\/\\/[\\w\\-]+(\\.[\\w\\-]+)+[\\w\\-.,@?^=%&:/~+#]*' },
    { label: 'Phone (US)', pattern: '\\(?\\d{3}\\)?[-.\\s]?\\d{3}[-.\\s]?\\d{4}' },
    { label: 'IP Address', pattern: '\\b(?:\\d{1,3}\\.){3}\\d{1,3}\\b' },
    { label: 'Date (YYYY-MM-DD)', pattern: '\\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\\d|3[01])' },
];

export default class RegexTesterComponent extends Component<{ feature?: string }, RegexState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private saveTimer: ReturnType<typeof setTimeout> | null = null;

    state: RegexState = (() => {
        const def = { pattern: '', testString: '', flags: { g: true, i: false, m: false, s: false, u: false } };
        const r = PersistedInput.load(STORAGE_KEY, def, isValidPersisted);
        return {
            pattern: r.pattern,
            testString: r.testString,
            flags: { g: !!r.flags.g, i: !!r.flags.i, m: !!r.flags.m, s: !!r.flags.s, u: !!r.flags.u },
            error: '',
            copiedField: null,
        };
    })();

    componentDidUpdate(_: any, prev: RegexState) {
        if (prev.pattern !== this.state.pattern
            || prev.testString !== this.state.testString
            || prev.flags !== this.state.flags) {
            if (this.saveTimer) clearTimeout(this.saveTimer);
            this.saveTimer = setTimeout(() => {
                PersistedInput.save(STORAGE_KEY, {
                    pattern: this.state.pattern,
                    testString: this.state.testString,
                    flags: this.state.flags,
                });
            }, 500);
        }
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
        if (this.saveTimer) clearTimeout(this.saveTimer);
    }

    buildRegex(): RegExp | null {
        const { pattern, flags } = this.state;
        if (!pattern) return null;
        const flagStr = Object.entries(flags).filter(([, v]) => v).map(([k]) => k).join('');
        try {
            return new RegExp(pattern, flagStr);
        } catch (e: unknown) {
            return null;
        }
    }

    getError(): string {
        const { pattern, flags } = this.state;
        if (!pattern) return '';
        const flagStr = Object.entries(flags).filter(([, v]) => v).map(([k]) => k).join('');
        try {
            new RegExp(pattern, flagStr);
            return '';
        } catch (e: unknown) {
            return e instanceof Error ? e.message : 'Invalid regex';
        }
    }

    getMatches(): RegExpExecArray[] {
        const regex = this.buildRegex();
        if (!regex || !this.state.testString) return [];
        const matches: RegExpExecArray[] = [];
        if (regex.global) {
            let m: RegExpExecArray | null;
            while ((m = regex.exec(this.state.testString)) !== null) {
                matches.push(m);
                if (m.index === regex.lastIndex) regex.lastIndex++;
            }
        } else {
            const m = regex.exec(this.state.testString);
            if (m) matches.push(m);
        }
        return matches;
    }

    buildHighlightedHtml(matches: RegExpExecArray[]): string {
        const { testString } = this.state;
        if (!matches.length) return this.escapeHtml(testString);
        const sorted = [...matches].sort((a, b) => a.index - b.index);
        let result = '';
        let lastIndex = 0;
        for (const m of sorted) {
            if (m.index < lastIndex) continue;
            result += this.escapeHtml(testString.slice(lastIndex, m.index));
            result += `<mark>${this.escapeHtml(m[0])}</mark>`;
            lastIndex = m.index + m[0].length;
        }
        result += this.escapeHtml(testString.slice(lastIndex));
        return result;
    }

    escapeHtml(s: string): string {
        return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    toggleFlag = (flag: keyof RegexState['flags']) => {
        this.setState(prev => ({ flags: { ...prev.flags, [flag]: !prev.flags[flag] } }));
    };

    applyPreset = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const preset = PRESETS[Number(e.target.value)];
        if (preset?.pattern) this.setState({ pattern: preset.pattern, error: '' });
    };

    copyMatches = (matches: RegExpExecArray[]) => {
        const text = matches.map(m => m[0]).join('\n');
        navigator.clipboard.writeText(text).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: 'matches' });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    render() {
        const { pattern, testString, flags, copiedField } = this.state;
        const error = this.getError();
        const matches = error ? [] : this.getMatches();
        const highlightedHtml = this.buildHighlightedHtml(matches);

        const groups = matches.flatMap((m, mi) =>
            m.slice(1).map((val, gi) => ({ match: mi + 1, group: gi + 1, value: val ?? '(no match)' }))
        );

        return (
            <FullscreenHost>
            <div className="row">
                <div className="col-12">
                    <div className={styles.topRow}>
                        <input
                            className={`${styles.regexInput} ${error ? sharedStyles.inputError : ''}`}
                            placeholder="Enter regex pattern..."
                            value={pattern}
                            onChange={e => this.setState({ pattern: e.target.value })}
                            aria-label="Regex pattern"
                        />
                        <select className={styles.presetSelect} onChange={this.applyPreset} defaultValue="0" aria-label="Regex presets">
                            {PRESETS.map((p, i) => <option key={i} value={i}>{p.label}</option>)}
                        </select>
                    </div>
                    {error && <div className={sharedStyles.errorMessage} aria-live="polite">⚠ {error}</div>}

                    <div className={styles.flags}>
                        {(Object.keys(flags) as (keyof typeof flags)[]).map(f => (
                            <label key={f} className={styles.flagLabel}>
                                <input type="checkbox" checked={flags[f]} onChange={() => this.toggleFlag(f)} />
                                {f}
                            </label>
                        ))}
                    </div>

                    <label className={sharedStyles.label}>Test String</label>
                    <textarea
                        className={sharedStyles.textarea}
                        placeholder="Enter text to test against..."
                        value={testString}
                        onChange={e => this.setState({ testString: e.target.value })}
                        rows={5}
                    />

                    {testString && (
                        <>
                            <label className={sharedStyles.label} style={{ marginTop: '1rem' }}>Match Preview</label>
                            <div className={styles.preview} dangerouslySetInnerHTML={{ __html: highlightedHtml || '<span style="color:#94a3b8">No matches</span>' }} />
                        </>
                    )}

                    <div className={styles.matchInfo}>
                        Matches:<strong>{matches.length}</strong>
                    </div>

                    {matches.length > 0 && (
                        <div className={sharedStyles.actions}>
                            <button
                                className={`${sharedStyles.copyButton} ${copiedField === 'matches' ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={() => this.copyMatches(matches)}
                            >
                                {copiedField === 'matches' ? '✓ Copied' : 'Copy Matches'}
                            </button>
                        </div>
                    )}

                    {groups.length > 0 && (
                        <>
                            <label className={sharedStyles.label} style={{ marginTop: '1rem' }}>Capture Groups</label>
                            <table className={styles.groupTable}>
                                <thead>
                                    <tr><th>Match #</th><th>Group #</th><th>Value</th></tr>
                                </thead>
                                <tbody>
                                    {groups.map((g, i) => (
                                        <tr key={i}><td>{g.match}</td><td>{g.group}</td><td>{g.value}</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>
            </div>
            </FullscreenHost>
        );
    }
}
